import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Space, Table, Tag } from '@arco-design/web-react';
import { useNavigate } from 'react-router-dom';
import { customersApi } from '@/api';
import { formatMoney } from '@/utils/money';
import type { CustomerListResult } from '@/api';
import type { CustomerSummary } from '@/types/customer';
import WalletTransferModal from './WalletTransferModal';
import ResetPasswordModal from './ResetPasswordModal';

export default function CustomersView() {
  const navigate = useNavigate();
  const [data, setData] = useState<CustomerListResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [transferTarget, setTransferTarget] = useState<CustomerSummary | null>(null);
  const [resetTarget, setResetTarget] = useState<CustomerSummary | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    customersApi
      .list({ keyword: keyword || undefined, page, pageSize })
      .then(setData)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [keyword, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <div className="admin-page__title">客户中心</div>
          <div className="admin-page__subtitle">
            管理客户登录账号、母账户余额与基础信息（敏感字段默认脱敏）
          </div>
        </div>
        <Space size={12}>
          <div className="admin-stat" style={{ padding: '12px 20px' }}>
            <div className="admin-stat__label">母账户余额</div>
            <div className="admin-stat__value" style={{ fontSize: 20 }}>
              {data && data.masterBalanceCents !== null
                ? formatMoney(data.masterBalanceCents)
                : '—'}
            </div>
          </div>
          <div className="admin-stat" style={{ padding: '12px 20px' }}>
            <div className="admin-stat__label">本次查询客户余额合计</div>
            <div className="admin-stat__value" style={{ fontSize: 20 }}>
              {data ? formatMoney(data.totalWalletBalanceCents) : '-'}
            </div>
          </div>
        </Space>
      </div>

      <div className="admin-card">
        <Space style={{ marginBottom: 12 }}>
          <Input.Search
            placeholder="搜索用户名 / 邮箱（脱敏匹配）"
            allowClear
            style={{ width: 320 }}
            value={keyword}
            onChange={setKeyword}
            onSearch={() => {
              setPage(1);
              load();
            }}
          />
          <Button onClick={load}>刷新</Button>
        </Space>
        <Table<CustomerSummary>
          loading={loading}
          rowKey="id"
          data={data?.items ?? []}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            onChange: (p) => setPage(p),
            showTotal: true,
          }}
          columns={[
            { title: '用户名', dataIndex: 'username' },
            { title: '邮箱（脱敏）', dataIndex: 'emailMasked' },
            {
              title: '手机（脱敏）',
              dataIndex: 'phoneMasked',
              render: (v) => v || '-',
            },
            {
              title: '母账户余额',
              dataIndex: 'walletBalanceCents',
              align: 'right',
              render: (v: number) => formatMoney(v),
            },
            { title: '卡片数', dataIndex: 'cardCount', align: 'right' },
            {
              title: '状态',
              dataIndex: 'enabled',
              render: (v: boolean) =>
                v ? <Tag color="green">启用</Tag> : <Tag color="red">停用</Tag>,
            },
            {
              title: '操作',
              dataIndex: 'op',
              width: 280,
              render: (_, row) => (
                <Space>
                  <Button size="mini" onClick={() => navigate(`/customers/${row.id}`)}>
                    详情
                  </Button>
                  <Button
                    size="mini"
                    type="primary"
                    onClick={() => setTransferTarget(row)}
                  >
                    划拨
                  </Button>
                  <Button size="mini" onClick={() => setResetTarget(row)}>
                    改密
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </div>

      <WalletTransferModal
        customer={transferTarget}
        visible={!!transferTarget}
        onClose={() => setTransferTarget(null)}
        onSuccess={load}
      />
      <ResetPasswordModal
        customer={resetTarget}
        visible={!!resetTarget}
        onClose={() => setResetTarget(null)}
      />
    </div>
  );
}
